/**
 * Pure settings-payload builders for the Appearance editor's SPLIT saves
 * (owner 2026-09-25: Site design / Logo / Banner are exclusive tabbed
 * subsections, each with its own Save). The field partition is the contract:
 *
 *  - Site design owns: homeTemplate, heroPremier/heroClassic, fonts (incl.
 *    the INT-0056 body font),
 *    heroFocal, and banner.colorPaletteKey (merged INTO the existing banner
 *    sub-object — never dropping BannerEditor's keys).
 *  - Logo owns: heroLogo (emphasis) — and nothing else; the logo image
 *    itself persists through its own upload/variant endpoints.
 *  - Banner is BannerEditor's own save (settings.banner), not built here.
 *
 * Both builders spread the CURRENT saved settings, so callers must pass a
 * FRESH `saved` (the store org is refreshed after every save — the old
 * single-save flow spread a stale snapshot and could clobber a
 * just-saved banner).
 */

import { CUSTOM_PALETTE_KEY, type CustomColors } from '@staty/shared/orgAppearance';

type AnyDoc = Record<string, any>;

export interface SiteDesignDraft {
  template: string;
  paletteKey: string;
  heroPremier: string;
  heroClassic: string;
  fontPremier: string;
  fontClassic: string;
  /** INT-0056: site body font key (BODY_FONTS), one for every template. */
  fontBody: string;
  /** INT-0057: normalized pair, used when paletteKey is 'custom'; null = invalid/absent. */
  customColors: CustomColors | null;
  heroFocal: { x: number; y: number };
}

export function buildSiteDesignSettings(saved: AnyDoc, d: SiteDesignDraft): AnyDoc {
  return {
    ...saved,
    homeTemplate: d.template,
    heroPremier: d.heroPremier || undefined,
    heroClassic: d.heroClassic,
    fonts: { ...(saved.fonts || {}), premier: d.fontPremier, classic: d.fontClassic, body: d.fontBody },
    heroFocal: d.heroFocal,
    // INT-0057: 'custom' writes the pair next to the key; re-selecting a
    // curated palette keeps the saved pair so it round-trips.
    banner: d.paletteKey
      ? {
        ...(saved.banner || {}),
        colorPaletteKey: d.paletteKey,
        ...(d.paletteKey === CUSTOM_PALETTE_KEY && d.customColors ? { customColors: d.customColors } : {}),
      }
      : saved.banner,
  };
}

export function buildLogoSettings(saved: AnyDoc, d: { heroLogo: 'standard' | 'prominent' }): AnyDoc {
  return { ...saved, heroLogo: d.heroLogo };
}
